<template>
  <v-card flat>
    <v-card-title class="py-0 px-0">
      <v-toolbar
        :height="$vuetify.breakpoint.xsOnly ? 44 : 60"
        class="filter-toolbar"
        flat
      >
        <v-btn
          v-if="posUserHasComponentPermission('Orders', 'ADD') && posUserHasComponentPermission('Pos', 'ACCESS')"
          :icon="$vuetify.breakpoint.xsOnly"
          :to="{name: 'Pos', exact: true}"
          class="primary--text elevation-0 ma-1"
          text
        >
          <v-icon :left="$vuetify.breakpoint.smAndUp">
            {{ $t('Order.OrdersAll.Toolbar.Button.Icon') }}
          </v-icon>&nbsp;
          <span v-if="$vuetify.breakpoint.smAndUp">{{
            $t('Order.OrdersAll.Toolbar.Button.Title').toLocaleUpperCase($i18n.locale)
          }}</span>
        </v-btn>

        <v-spacer />

        <table-actions
          v-if="userCanDeleteOrders"
          v-model="dataTable.tableActions"
          :disabled="!viewHasData"
          :items.sync="selectedOrders"
        >
          <v-btn
            v-if="userCanDeleteOrders"
            :color="$vuetify.theme.dark ? '#1E1E1E' : 'rgba(0, 0, 0, 0.06)'"
            :disabled="deleteOrderLoading || !selectedOrders.length"
            :loading="deleteOrderLoading"
            :style="$vuetify.breakpoint.xsOnly ? 'width: 36px; min-width: 36px; height: 36px;' : 'width: 48px; min-width: 48px; height: 48px;'"
            class="pa-0 ma-1 elevation-0"
            @click="onDeleteOrdersClick(selectedOrders)"
          >
            <v-icon
              color="red"
              v-text="'delete'"
            />
          </v-btn>
        </table-actions>

        <filter-search v-model="dataTable.search.terms" />

        <filter-options
          v-model="filtersModel"
          :filters="filterData"
          max-width="500"
        />
      </v-toolbar>
    </v-card-title>

    <!--TODAY ORDERS TABLE-->
    <v-data-table
      v-model="selectedOrders"
      :custom-filter="customSearch"
      :footer-props="dataTable.footerProps"
      :headers="dataTableHeaders"
      :height="dataTable.resize.bodyHeight"
      :items="filteredItems"
      :loading="dataTable.loading"
      :loading-text="dataTable.text.loadingText"
      :no-data-text="dataTable.text.noDataText"
      :no-results-text="dataTable.text.noResultsText"
      :options.sync="dataTable.options"
      :search="dataTable.search.terms"
      :show-select="dataTable.tableActions"
      :single-select="false"
      fixed-header
      item-key="Id"
    >
      <template #[`item.source_type`]="{item}">
        <div v-if="getOrderSourceTypeById(item.SourceTypeId)">
          <v-tooltip
            color="indigo lighten-2"
            right
          >
            <template #activator="{on}">
              <div v-on="on">
                <v-badge
                  v-model="item.IsTest"
                  bordered
                  color="deep-orange"
                  overlap
                >
                  <template #badge>
                    <span class="font-weight-bold">TEST</span>
                  </template>

                  <v-avatar size="28">
                    <v-icon
                      v-if="item.SourceTypeId < 100"
                      color="indigo lighten-2"
                    >
                      {{ getOrderSourceTypeById(item.SourceTypeId) && getOrderSourceTypeById(item.SourceTypeId).logo }}
                    </v-icon>

                    <img
                      v-else
                      :src="getOrderSourceTypeById(item.SourceTypeId) && getOrderSourceTypeById(item.SourceTypeId).logo"
                    >
                  </v-avatar>
                </v-badge>
              </div>
            </template>

            <div v-if="item.IsTest">
              {{ $t('Common.Misc.TestOrder') }}
            </div>
            <span v-if="item.SourceTypeId < 100">
              {{ getOrderSourceTypeLangById(item.SourceTypeId) && getOrderSourceTypeLangById(item.SourceTypeId).Desc }}
            </span>
            <span v-else>
              {{ getOrderSourceTypeById(item.SourceTypeId).desc }}
            </span>
          </v-tooltip>
        </div>
      </template>

      <template #[`item.daily_check`]="{item}">
        <v-chip
          v-if="item.DailyCheck && appConfig.LOCATION_DATA.ShowDailyCheck"
          class="px-1"
          label
          x-small
        >
          {{ item.DailyCheck }}
        </v-chip>
      </template>

      <template #[`item.order_id`]="{item}">
        <div class="d-flex align-center">
          <template v-if="item.ExternalPlatform && item.ExternalCatalogId && getExternalOrderRawUrl(item) && userIsSuperAdmin">
            <div class="d-inline-flex mr-2">
              <v-tooltip
                color="light-green"
                top
              >
                <template #activator="{on}">
                  <v-btn
                    :href="getExternalOrderRawUrl(item)"
                    class="light-green"
                    dark
                    icon
                    small
                    target="_blank"
                    v-on="on"
                  >
                    <v-icon small>
                      mdi-code-block-braces
                    </v-icon>
                  </v-btn>
                </template>

                <span>View External Order</span>
              </v-tooltip>
            </div>
          </template>

          <div class="d-inline-flex">
            <div>
              <a
                href="#"
                @click.prevent="getOrderDetails(item)"
              >{{ item.OrderCode }}</a>

              <template v-if="item.IsEdited">
                <v-tooltip
                  color="purple"
                  top
                >
                  <template #activator="{on}">
                    <v-avatar
                      class="ml-1"
                      color="purple"
                      size="16"
                      v-on="on"
                    >
                      <v-icon
                        dark
                        x-small
                        v-text="$t('Order.OrdersAll.ListView.Button.Edit.Icon')"
                      />
                    </v-avatar>
                  </template>

                  <span>{{ $t('Order.OrdersAll.ListView.Button.Edit.Edited') }}</span>
                </v-tooltip>
              </template>

              <div
                v-if="item.ExternalPlatform"
                class="text-caption text-uppercase"
              >
                #{{ item.ExternalOrderId }}
              </div>

              <div
                v-if="item.MarketplaceOrderCode"
                class="text-caption text-uppercase"
              >
                #{{ item.MarketplaceOrderCode }}
              </div>

              <div
                v-if="item.QrData"
                class="text-caption"
              >
                {{ item.QrData }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #[`item.icons`]="{item}">
        <v-tooltip
          :color="parseInt(item.CustomerId) === 0 ? 'grey darken-3' : 'amber darken-3'"
          right
        >
          <template #activator="{on}">
            <v-icon
              :color="parseInt(item.CustomerId) === 0 ? 'grey' : 'amber'"
              class="text--lighten-2"
              small
              v-on="on"
            >
              verified_user
            </v-icon>
          </template>
          <span>{{
            parseInt(item.CustomerId) === 0 ? $t('Customer.CustomerType.Visitor') : $t('Customer.CustomerType.Registered')
          }}</span>
        </v-tooltip>

        <div v-if="parseInt(item.AddressId) > -1 && item.Address && parseInt(item.OrderTypeId) === 1">
          <v-icon
            color="light-green lighten-1"
            small
          >
            location_on
          </v-icon>
        </div>

        <div v-if="item.Telephone && $options.filters.phone(item.Telephone)">
          <v-icon
            color="blue lighten-2"
            small
          >
            phone
          </v-icon>
        </div>

        <div v-if="item.Telephone2 && $options.filters.phone(item.Telephone2)">
          <v-icon
            color="blue lighten-2"
            small
          >
            phone
          </v-icon>
        </div>

        <div v-if="item.Telephone3 && $options.filters.phone(item.Telephone3)">
          <v-icon
            color="blue lighten-2"
            small
          >
            phone
          </v-icon>
        </div>
      </template>

      <template #[`item.customer`]="{item}">
        <div
          v-if="posUserHasComponentPermission('CustomerDetails', 'ACCESS') && parseInt(item.CustomerId) !== 0"
          :class="isDarkMode ? 'white--text' : 'grey--text text--darken-3'"
          :style="customerColumnMaxWidth"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer;"
          @click="$bus.$emit('show-customer-details-dialog', item.CustomerId)"
        >
          {{ item.FullName }}
        </div>

        <template v-else>
          <div
            :class="isDarkMode ? 'white--text' : 'grey--text text--darken-3'"
            :style="customerColumnMaxWidth"
            style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
          >
            {{
              parseInt(item.CustomerId) !== 0 || item.FullName.trim() ? item.FullName : $t('Customer.CustomerType.Visitor')
            }}
          </div>
        </template>

        <div
          v-if="!isQrOrder(item) && parseInt(item.AddressId) > -1 && item.Address && parseInt(item.OrderTypeId) === 1"
          :style="customerColumnMaxWidth"
          class="grey--text"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
        >
          <address-display :address="item?.Address" />
        </div>

        <div
          v-else-if="isQrOrder(item) && parseInt(item.OrderTypeId) === 1"
          :style="customerColumnMaxWidth"
          class="grey--text"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
        >
          {{ item.Comment }}
        </div>

        <div
          v-if="item.Telephone || item.Telephone2 || item.Telephone3"
          :style="customerColumnMaxWidth"
          class="grey--text"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
        >
          <template v-if="item.Telephone && $options.filters.phone(item.Telephone)">
            {{ item.Telephone | phone }}<br>
          </template>

          <template v-if="item.Telephone2 && $options.filters.phone(item.Telephone2)">
            {{ item.Telephone2 | phone }}<br>
          </template>

          <template v-if="item.Telephone3 && $options.filters.phone(item.Telephone3)">
            {{ item.Telephone3 | phone }}<br>
          </template>
        </div>
      </template>

      <template #[`item.staff_id`]="{item}">
        <user-chip
          v-if="item.StaffId > 0"
          :color="item.StaffColor"
          :full-name="item.StaffFirstname + ' ' + item.StaffLastname"
          :short-name="item.StaffShortname"
        />
      </template>

      <template #[`item.status`]="{item}">
        <div class="my-1">
          <order-status-chip
            :order="item"
            dense
            min-width="186"
          />
        </div>

        <div class="my-1">
          <order-status-actions
            v-if="item.ScheduledToday || item.ScheduledExpired || item.StatusId === OrderStatusEnum.Received"
            :can-update-order-time="false"
            :order="item"
          />
        </div>
      </template>

      <template #[`item.order_type`]="{item}">
        <div class="my-1">
          <order-type-chip
            :order="item"
            dense
          />
        </div>

        <div class="my-1">
          <order-delivery-status-chip
            :order="item"
            dense
          />
        </div>
      </template>

      <template #[`item.print_notify`]="{item}">
        <div
          v-if="!!item?.Prints?.length && isAppPrintLogVisible"
          class="my-1"
        >
          <template v-for="(print, idx) in item.Prints">
            <div :key="idx">
              <status-chip
                :avatar-color="getPrintNotifyStatus(print).AvatarColor"
                :color="getPrintNotifyStatus(print).Color"
                :icon="getPrintNotifyStatus(print).AvatarIcon"
                :label="getPrintNotifyStatus(print).Label"
                :min-width="180"
                :tooltip="getPrintNotifyStatus(print).Tooltip"
                x-dense
              />
            </div>
          </template>
        </div>
      </template>

      <template #[`item.order_total`]="{item}">
        <v-badge
          :class="{'pulse-badge': orderIsGeneratingFiscalDocument(item)}"
          :color="orderFiscalDocumentBadgeColor(item)"
          :value="appHasFiscalDocsEnabled && appHasFiscalDocsProvider && (orderHasFiscalDocument(item, true) || (orderCanGenerateFiscalDocument(item) && orderIsRefundable(item)) || orderIsGeneratingFiscalDocument(item) )"
          class="receipts-badge"
          offset-x="8"
          offset-y="2"
          right
        >
          <v-tooltip
            v-if="(appHasFiscalDocsEnabled && appHasFiscalDocsProvider) || orderHasFiscalDocument(item, true)"
            :color="orderFiscalDocumentBadgeColor(item)"
            content-class="text-center"
            max-width="350"
            right
          >
            <template #activator="{on}">
              <div
                class="d-inline-block"
                v-on="on"
              >
                <template v-if="orderHasUnpaidBalance(item) && parseInt(item.StatusId) !== OrderStatusEnum.Canceled">
                  <span class="red--text">{{ orderTransactionsTotal(item) | currency }}</span> <span>/</span> <span class="blue--text">{{ item.Price | currency }}</span>
                </template>
                <template v-else>
                  {{ item.Price | currency }}
                </template>
              </div>
            </template>

            <div v-if="orderHasUnpaidBalance(item) && parseInt(item.StatusId) !== OrderStatusEnum.Canceled">
              {{ $t('Order.Total.PartiallyPaid') }}
            </div>
            <div v-else-if="orderHasReceipt(item, orderLatestFiscalDocumentHasError(item)) && orderHasInvoice(item, orderLatestFiscalDocumentHasError(item))">
              <div>
                {{ $t('Order.Total.Receipt') }}

                <v-chip
                  v-if="orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                  class="px-2 ml-2 mt-1 float-end"
                  x-small
                >
                  {{ orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) }}
                </v-chip>
              </div>

              <div>
                {{ $t('Order.Total.Invoice') }}

                <v-chip
                  v-if="orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                  class="px-2 ml-2 mt-1 float-end"
                  x-small
                >
                  {{ orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) }}
                </v-chip>
              </div>
            </div>
            <div v-else-if="orderHasReceipt(item, orderLatestFiscalDocumentHasError(item))">
              {{ $t('Order.Total.Receipt') }}

              <v-chip
                v-if="orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                class="px-2 ml-2 mt-1 float-end"
                x-small
              >
                {{ orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) }}
              </v-chip>
            </div>
            <div v-else-if="orderHasInvoice(item, orderLatestFiscalDocumentHasError(item))">
              {{ $t('Order.Total.Invoice') }}

              <v-chip
                v-if="orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) > 0"
                class="px-2 ml-2 mt-1 float-end"
                x-small
              >
                {{ orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item)) }}
              </v-chip>
            </div>
            <div v-else-if="orderIsGeneratingFiscalDocument(item)">
              {{ $t('Order.Total.GeneratingDoc') }}
            </div>
            <div v-else>
              {{ $t('Order.Total.NoFiscalDoc') }}
            </div>

            <div
              v-if="orderLatestFiscalDocumentHasError(item)"
              class="mt-2"
            >
              <template v-for="receipt in orderFiscalDocuments(item, true)">
                <div
                  :key="`receipt-${receipt.ReceiptId}`"
                  class="mb-1"
                >
                  <template v-for="(error, index) in orderFiscalDocumentErrors(receipt)">
                    <div
                      :key="`receipt-${receipt.ReceiptId}-error-${index}`"
                      class="text-left mb-1"
                    >
                      <span class="font-weight-bold">{{ error.code }}</span> - {{ error.message }}
                    </div>
                  </template>
                </div>
              </template>
            </div>
          </v-tooltip>

          <div
            v-else
            class="d-inline-block"
          >
            <template v-if="orderHasUnpaidBalance(item) && parseInt(item.StatusId) !== OrderStatusEnum.Canceled">
              <span class="red--text">{{ orderTransactionsTotal(item) | currency }}</span> <span>/</span> <span class="blue--text">{{ item.Price | currency }}</span>
            </template>
            <template v-else>
              {{ item.Price | currency }}
            </template>
          </div>

          <template #badge>
            {{ (orderReceiptsCount(item, orderLatestFiscalDocumentHasError(item)) + orderInvoicesCount(item, orderLatestFiscalDocumentHasError(item))) || '' }}
          </template>
        </v-badge>
      </template>

      <template #[`item.order_payment`]="{item}">
        <order-payments-icon
          :item="item"
          chip
          class="ml-1"
          dense
        />
      </template>

      <template #[`item.date_added`]="{item}">
        <span>{{ item.DateAdded }}</span>

        <div class="grey--text">
          {{ item.TimeAdded }}
        </div>
      </template>

      <template #[`item.date_scheduled`]="{item}">
        <span>{{ item.ScheduledDateAt }}</span>

        <div class="grey--text">
          {{ item.ScheduledTimeAt }}
        </div>
      </template>

      <template #[`item.actions`]="{item}">
        <v-btn
          v-if="dataTable.tableActions"
          :small="$vuetify.breakpoint.xsOnly"
          class="blue-grey lighten-2 elevation-2 ma-0"
          dark
          disabled
          icon
        >
          <v-icon :small="$vuetify.breakpoint.xsOnly">
            menu
          </v-icon>
        </v-btn>

        <v-speed-dial
          v-else
          v-model="item.fab"
          direction="left"
          transition="slide-x-reverse-transition"
        >
          <template #activator>
            <v-btn
              :small="$vuetify.breakpoint.xsOnly"
              class="blue-grey lighten-2 elevation-2 ma-0"
              dark
              icon
            >
              <v-icon
                v-if="item.fab"
                :small="$vuetify.breakpoint.xsOnly"
              >
                close
              </v-icon>
              <v-icon
                v-else
                :small="$vuetify.breakpoint.xsOnly"
              >
                menu
              </v-icon>
            </v-btn>
          </template>

          <print-button
            v-if="printOrderDocumentVisible(item)"
            :small="$vuetify.breakpoint.xsOnly"
            :tooltip-open-delay="150"
            hide-label
            icon
            menu-offset-y
            open-on-hover
            tooltip-position="top"
            @printer-selected="printer => printOrder(item, printer)"
          />

          <print-preparation-button
            v-if="printOrderPreparationVisible(item)"
            :order-print-groups="item.PrintGroups || []"
            :small="$vuetify.breakpoint.xsOnly"
            :tooltip-open-delay="150"
            hide-label
            icon
            menu-offset-y
            open-on-hover
            tooltip-position="top"
            @printer-group-selected="printerGroup => printOrderPreparation(item, printerGroup)"
          />

          <template v-if="parseInt(item.SourceTypeId) !== SourceTypeEnum.TABLE">
            <template
              v-if="parseInt(item.DeliveryStaffId) === -1 && parseInt(item.StatusId) !== OrderStatusEnum.NotComplete && posUserHasComponentPermission('Orders', 'MANAGE') && posUserHasComponentPermission('DeliveryStaff', 'MANAGE')"
            >
              <v-tooltip
                :color="$t('Order.OrdersToday.ListView.Button.AddTo.Color')"
                top
              >
                <template #activator="{on}">
                  <v-btn
                    :class="$t('Order.OrdersToday.ListView.Button.AddTo.Color')"
                    :small="$vuetify.breakpoint.xsOnly"
                    dark
                    icon
                    @click="showAddOrderToDeliveryStaff(item)"
                    v-on="on"
                  >
                    <v-icon :small="$vuetify.breakpoint.xsOnly">
                      {{ $t('Order.OrdersToday.ListView.Button.AddTo.Icon') }}
                    </v-icon>
                  </v-btn>
                </template>

                <span>{{ $t('Order.OrdersToday.ListView.Button.AddTo.Title') }}</span>
              </v-tooltip>
            </template>

            <template
              v-if="parseInt(item.DeliveryStaffId) > -1 && deliveryStaffHasActiveShift(item) && parseInt(item.StatusId) !== OrderStatusEnum.NotComplete && posUserHasComponentPermission('Orders', 'MANAGE') && posUserHasComponentPermission('DeliveryStaff', 'MANAGE')"
            >
              <v-tooltip
                :color="$t('Order.OrdersToday.ListView.Button.RemoveFrom.Color')"
                content-class="text-center"
                top
              >
                <template #activator="{on}">
                  <v-btn
                    :class="$t('Order.OrdersToday.ListView.Button.RemoveFrom.Color')"
                    :small="$vuetify.breakpoint.xsOnly"
                    dark
                    icon
                    @click="showRemoveOrderFromDeliveryStaffDialog(item)"
                    v-on="on"
                  >
                    <v-icon :small="$vuetify.breakpoint.xsOnly">
                      {{ $t('Order.OrdersToday.ListView.Button.RemoveFrom.Icon') }}
                    </v-icon>
                  </v-btn>
                </template>

                <span>{{
                  $t('Order.OrdersToday.ListView.Button.RemoveFrom.Title')
                }}<br>{{
                  item.DeliveryStaffFirstname + ' ' + item.DeliveryStaffLastname | sanitizeDiacritics | uppercase
                }}</span>
              </v-tooltip>
            </template>

            <template v-if="!SourceTypeEnum.IS_MARKETPLACE_SOURCE(item.SourceTypeId) && canEditOrder(item) && parseInt(item.SourceTypeId) !== SourceTypeEnum.TABLE">
              <v-tooltip
                :color="$t('Order.OrdersAll.ListView.Button.Edit.Color')"
                top
              >
                <template #activator="{on}">
                  <v-btn
                    :class="$t('Order.OrdersAll.ListView.Button.Edit.Color')"
                    :small="$vuetify.breakpoint.xsOnly"
                    dark
                    icon
                    @click="showOrderEditDialog(item)"
                    v-on="on"
                  >
                    <v-icon :small="$vuetify.breakpoint.xsOnly">
                      {{ $t('Order.OrdersAll.ListView.Button.Edit.Icon') }}
                    </v-icon>
                  </v-btn>
                </template>

                <span>{{ $t('Order.OrdersAll.ListView.Button.Edit.Title') }}</span>
              </v-tooltip>
            </template>

            <v-tooltip
              :color="$t('Order.OrdersAll.ListView.Button.View.Color')"
              top
            >
              <template #activator="{on}">
                <v-btn
                  :class="$t('Order.OrdersAll.ListView.Button.View.Color')"
                  :small="$vuetify.breakpoint.xsOnly"
                  dark
                  icon
                  @click="getOrderDetails(item)"
                  v-on="on"
                >
                  <v-icon :small="$vuetify.breakpoint.xsOnly">
                    {{ $t('Order.OrdersAll.ListView.Button.View.Icon') }}
                  </v-icon>
                </v-btn>
              </template>

              <span>{{ $t('Order.OrdersAll.ListView.Button.View.Title') }}</span>
            </v-tooltip>

            <template v-if="parseInt(item.StatusId) !== OrderStatusEnum.Canceled && posUserHasComponentPermission('Orders', 'DELETE')">
              <v-tooltip
                :color="$t('Order.OrdersAll.ListView.Button.Cancel.Color')"
                top
              >
                <template #activator="{on}">
                  <v-btn
                    :class="$t('Order.OrdersAll.ListView.Button.Cancel.Color')"
                    :small="$vuetify.breakpoint.xsOnly"
                    dark
                    icon
                    @click="onCancelOrderClick(item)"
                    v-on="on"
                  >
                    <v-icon :small="$vuetify.breakpoint.xsOnly">
                      {{ $t('Order.OrdersAll.ListView.Button.Cancel.Icon') }}
                    </v-icon>
                  </v-btn>
                </template>

                <span>{{ $t('Order.OrdersAll.ListView.Button.Cancel.Title') }}</span>
              </v-tooltip>
            </template>

            <template
              v-if="!SourceTypeEnum.IS_MARKETPLACE_SOURCE(item.SourceTypeId) && (parseInt(item.StatusId) === OrderStatusEnum.Completed || parseInt(item.StatusId) === OrderStatusEnum.Canceled) && userCanDeleteOrder"
            >
              <v-tooltip
                :color="$t('Order.OrdersAll.ListView.Button.Delete.Color')"
                top
              >
                <template #activator="{on}">
                  <v-btn
                    :class="$t('Order.OrdersAll.ListView.Button.Delete.Color')"
                    :small="$vuetify.breakpoint.xsOnly"
                    dark
                    icon
                    @click="onDeleteOrderClick(item)"
                    v-on="on"
                  >
                    <v-icon :small="$vuetify.breakpoint.xsOnly">
                      {{ $t('Order.OrdersAll.ListView.Button.Delete.Icon') }}
                    </v-icon>
                  </v-btn>
                </template>

                <span>{{ $t('Order.OrdersAll.ListView.Button.Delete.Title') }}</span>
              </v-tooltip>
            </template>
          </template>
        </v-speed-dial>
      </template>
    </v-data-table>

    <order-change-status-dialog
      :cancel-order-confirm-dialog-visible.sync="cancelOrderConfirmDialogVisible"
      :selected-order="!Array.isArray(selectedOrder) ? selectedOrder : undefined"
    />

    <order-delete-dialog
      v-if="appConfig.LOCATION_DATA.HasForceDeleteOrders && posUserHasComponentPermission('Orders', 'DELETE')"
      :is-delete-and-copy-order="isDeleteAndCopyOrder"
      :selected-order="selectedOrder"
      :visible.sync="deleteOrderConfirmDialogVisible"
      @order-deleted="onOrdersDelete"
    />

    <confirm-order-edit-dialog
      :selected-order="selectedOrder"
      :visible.sync="confirmDialogEditOrderVisible"
      @order-edit="onEditAdvanced"
      @order-edit-copy="onEditCopy"
      @order-edit-delete="onEditDelete"
    />

    <confirm-dialog
      :html-content="`${$t('Order.OrdersToday.ListView.Button.RemoveFrom.PopUp.Content')} <strong>${selectedOrder ? selectedOrder.DeliveryStaffFirstname : ''} ${selectedOrder ? selectedOrder.DeliveryStaffLastname : ''}</strong>;`"
      :html-title="$t('Order.OrdersToday.ListView.Button.RemoveFrom.PopUp.Title')"
      :visible.sync="confirmDialogRemoveOrderFromDeliveryStaffVisible"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="delete-order-on-yes-btn"
      @delete-order-on-yes-btn="removeOrderFromDeliveryStaff"
    />
  </v-card>
</template>

<script>
import OrdersCommon from '@/mixins/orders/common'
import PrintButton from '@/components/common/print/PrintButton.vue'
import Translatable from '@/mixins/translatable'
import FilterCommon from '@/mixins/filter/filterCommon'
import FilterOptions from '@/components/common/filter/FilterOptions.vue'
import PrintPreparationButton from '@/components/common/print/PrintPreparationButton.vue'
import DataTable from '@/mixins/data-table/dataTable'
import FilterSearch from '@/components/common/filter/FilterSearch.vue'
import greekUtils from '@/lib/greek-utils'
import OrderStatusEnum from '@/api/Enums/OrderStatusEnum'
import TableActions from '@/components/common/filter/TableActions.vue'
import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum'
import OrderPaymentsIcon from '@/components/orders/common/OrderPaymentsIcon.vue'
import SourceTypeEnum from '@/api/Enums/SourceTypeEnum'
import PinManagerEnum from '@/api/Enums/PinManagerEnum'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import {getContrastColor} from '@/lib/color'
import OrderStatusChip from '@/components/orders/common/OrderStatusChip.vue'
import OrderTypeChip from '@/components/orders/common/OrderTypeChip.vue'
import OrderDeliveryStatusChip from '@/components/orders/common/OrderDeliveryStatusChip.vue'
import StatusChip from '@/components/common/StatusChip.vue'
import OrderStatusActions from '@/components/orders/common/OrderStatusActions.vue'
import AddressDisplay from '@/components/common/AddressDisplay.vue'
import ConfirmOrderEditDialog from '@/components/orders/ConfirmOrderEditDialog.vue'
import UserChip from '@/components/common/UserChip.vue'

export default {
  components: {
    UserChip,
    ConfirmOrderEditDialog,
    AddressDisplay,
    OrderStatusActions,
    StatusChip,
    OrderDeliveryStatusChip,
    OrderTypeChip,
    OrderStatusChip,
    OrderPaymentsIcon,
    TableActions,
    FilterSearch,
    PrintPreparationButton,
    FilterOptions,
    PrintButton
  },
  directives: {},
  mixins    : [OrdersCommon, Translatable, FilterCommon, DataTable],
  data() {
    return {
      dataTable: {
        dataStore    : 'ordersDataScheduled',
        socketCommand: {
          list: window.SocketCommand.Order.ScheduledList
        },
        resize: {
          enabled        : true,
          debounceTimeout: null,
          offset         : -12,
          bodyHeight     : undefined
        },
        options: {
          serverSideEnabled: false,
          defaultSortBy    : 'order_id',
          sortBy           : ['order_id'],
          sortDesc         : [true]
        },
        rejectHeaders: {
          xsOnly   : ['icons'],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: []
      },

      filtersModel: {
        lang           : 'Order.OrdersAll',
        filterOrderType: {
          value: 0,
          items: this.$t('Order.OrdersAll.Filter.filterOrderType.Items')
        },
        filterOrderReceipt: {
          value   : 0,
          items   : this.$t('Order.OrdersAll.Filter.filterOrderReceipt.Items'),
          disabled: true
        },
        filterHasDeliveryStaff: {
          value: 0,
          items: this.$t('Order.OrdersAll.Filter.filterHasDeliveryStaff.Items')
        },
        filterOrderStatus: {
          value: [],
          items: this.$t('Order.OrdersAll.Filter.filterOrderStatus.Items').filter(o => o.Id > 0)
        },
        filterSourceType: {
          value: null,
          items: []
        },
        filterPaymentType: {
          value: null,
          items: []
        },
        filterStaff: {
          value: null,
          items: []
        }
      },
      selectedDeliveryStaff                           : null,
      selectedDeliveryStaffDialogVisible              : false,
      confirmDialogRemoveOrderFromDeliveryStaffVisible: false,
      ordersMapDialogVisible                          : false,
      selectDateRangeDialogVisible                    : false,
      selectedPrinter                                 : null
    }
  },
  computed: {
    FiscalDocsProviderTypeEnum() {
      return FiscalDocsProviderTypeEnum
    },
    SourceTypeEnum() {
      return SourceTypeEnum
    },
    PaymentTypeEnum() {
      return PaymentTypeEnum
    },
    OrderStatusEnum() {
      return OrderStatusEnum
    },
    FiscalDocTypeEnum() {
      return FiscalDocTypeEnum
    },
    customerColumnMaxWidth() {
      return this.$vuetify.breakpoint.lgAndDown ? 'max-width: 200px; width: 200px;' : 'max-width: 310px;  width: 310px;'
    },
    filteredItems() {
      const items = this.dataTable?.data || []
      const filterOrderType = this.filtersModel.filterOrderType.value
      const filterHasDeliveryStaff = this.filtersModel.filterHasDeliveryStaff.value
      const filterOrderReceipt = this.filtersModel.filterOrderReceipt.value
      const filterSourceType = this.filtersModel.filterSourceType.value
      const filterPaymentType = this.filtersModel.filterPaymentType.value
      const filterOrderStatus = this.filtersModel.filterOrderStatus.value
      const filterStaff = this.filtersModel.filterStaff.value

      return items
        .map(o => {
          o.isSelectable = (parseInt(o.StatusId) === OrderStatusEnum.Completed || parseInt(o.StatusId) === OrderStatusEnum.Canceled) && this.userCanDeleteOrders
          return o
        })
        .filter(o => this.dailyOrdersVisibleStatuses.includes(o.StatusId))
        .filter(o => filterOrderType ? o.OrderTypeId === filterOrderType && parseInt(o.SourceTypeId) !== 4 : true)
        .filter(o => filterStaff ? o.StaffId === filterStaff : true)
        .filter(o => filterHasDeliveryStaff ? filterHasDeliveryStaff === 1 ? o.DeliveryStaffId > -1 : o.DeliveryStaffId === -1 : true)
        .filter(o => filterOrderReceipt ? filterOrderReceipt === 1 ? this.orderHasFiscalDocument(o) : !this.orderHasFiscalDocument(o) : true)
        .filter(o => filterSourceType ? o.SourceTypeId === filterSourceType : true)
        .filter(o => filterPaymentType ? this.orderHasPaymentTypeTransaction(o, filterPaymentType) : true)
        .filter(o => filterOrderStatus?.length > 0 ? filterOrderStatus?.includes(o.StatusId) && !o.DeletedAt : true)
    },
    staffList() {
      const data = this.dataTable?.data || []
      const list = data.map(item => {
        return {
          StaffId       : item.StaffId,
          StaffFullname : `${item.StaffFirstname} ${item.StaffLastname}`,
          StaffFirstname: item.StaffFirstname,
          StaffLastname : item.StaffLastname
        }
      })

      return [...new Set(list)]
    },
    deliveryStaffList() {
      return window.Vue?.deliveryStaffItems || []
    },
    dailyOrdersVisibleStatuses() {
      return this.posUser?.Settings?.Orders?.VisibleStatuses || [OrderStatusEnum.Received, OrderStatusEnum.Accepted, OrderStatusEnum.Preparation, OrderStatusEnum.Delivery, OrderStatusEnum.Completed, OrderStatusEnum.Canceled]
    }
  },
  watch: {
    selectedDeliveryStaff(newVal) {
      if (newVal) {
        window.callAS(window.SocketCommand.Staff.Delivery.Shift.AddOrder, {
          Id     : newVal.Id,
          OrderId: this.selectedOrder.Id
        }, window.SocketCommand.Staff.Delivery.All)
        this.selectedOrder = null
        this.selectedDeliveryStaff = null
      }
    }
  },

  created() {
    // console.log('1. created');
    this.$bus.$on('show-add-order-to-delivery-staff-dialog', this.showAddOrderToDeliveryStaff)
    this.$bus.$on('show-remove-order-from-delivery-staff-dialog', this.showRemoveOrderFromDeliveryStaffDialog)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.All, this.getData)
    this.$bus.$on(window.SocketCommand.Receipt.Update, this.getData)
    this.$bus.$on(window.SocketCommand.Order.Payment.Transaction.OrderResult, this.onPaymentTransactionResult)
    this.$bus.$on(window.SocketCommand.Order.DailyList, this.onUpdateSelectedOrder)
    this.$bus.$on(window.SocketCommand.Order.DailyGet, this.getData)

    // Init Custom Filter Values
    this.filtersModel.filterSourceType.items = this.sourceTypesList
    this.filtersModel.filterPaymentType.items = this.paymentTypesFiltersList
    this.filtersModel.filterOrderReceipt.disabled = !this.appHasFiscalDocsProvider
    this.filtersModel.filterStaff.items = this.staffList

    this.dataTable.headers = [
      {
        text     : this.$t('Order.Headers.SourceType'),
        value    : 'source_type',
        align    : 'left',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : '',
        value    : 'daily_check',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1',
        width    : 0
      },
      {
        text     : this.$t('Order.Headers.OrderId'),
        value    : 'order_id',
        align    : 'left',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : '',
        value    : 'icons',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : this.$t('Order.Headers.Customer'),
        value    : 'customer',
        align    : 'left',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : this.$t('Order.Headers.User'),
        value    : 'staff_id',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : this.$t('Order.Headers.Status'),
        value    : 'status',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : this.$t('Order.Headers.OrderType'),
        value    : 'order_type',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      ...this.isAppPrintLogVisible ? [{
        text     : this.$t('Order.Headers.PrintNotify'),
        value    : 'print_notify',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      }] : [],
      {
        text     : this.$t('Order.Headers.Total'),
        value    : 'order_total',
        align    : 'right',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : '',
        value    : 'order_payment',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1',
        width    : 25
      },
      {
        text     : this.$t('Order.Headers.DateAdded'),
        value    : 'date_added',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : this.$t('Order.Headers.DateScheduled'),
        value    : 'date_scheduled',
        align    : 'center',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1'
      },
      {
        text     : '',
        value    : 'actions',
        align    : 'right',
        sortable : false,
        class    : 'px-1',
        cellClass: 'text-caption px-1',
        width    : 40
      }
    ]
  },
  mounted() {
    // console.log('2. mounted');
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off('show-add-order-to-delivery-staff-dialog', this.showAddOrderToDeliveryStaff)
    this.$bus.$off('show-remove-order-from-delivery-staff-dialog', this.showRemoveOrderFromDeliveryStaffDialog)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.All, this.getData)
    this.$bus.$off(window.SocketCommand.Receipt.Update, this.getData)
    this.$bus.$off(window.SocketCommand.Order.Payment.Transaction.OrderResult, this.onPaymentTransactionResult)
    this.$bus.$off(window.SocketCommand.Order.DailyList, this.onUpdateSelectedOrder)
    this.$bus.$off(window.SocketCommand.Order.DailyGet, this.getData)
  },
  methods: {
    getContrastColor,

    customSearch(value, search, item) {
      if (!search) return

      const searchString = greekUtils.toGreeklish(String(search).toLocaleLowerCase(this.$i18n.locale))

      const itemString = [
        item.Id && String(item.Id),
        item.Email && String(item.Email),
        item.Address && greekUtils.toGreeklish(JSON.stringify(item.Address).toLocaleLowerCase(this.$i18n.locale)),
        item.FirstName && greekUtils.toGreeklish(String(item.FirstName).toLocaleLowerCase(this.$i18n.locale)),
        item.LastName && greekUtils.toGreeklish(String(item.LastName).toLocaleLowerCase(this.$i18n.locale)),
        item.StaffFirstname && greekUtils.toGreeklish(String(item.StaffFirstname).toLocaleLowerCase(this.$i18n.locale)),
        item.StaffLastname && greekUtils.toGreeklish(String(item.StaffLastname).toLocaleLowerCase(this.$i18n.locale)),
        item.Table?.TableAreaName && greekUtils.toGreeklish(String(item.Table.TableAreaName).toLocaleLowerCase(this.$i18n.locale)),
        item.Table?.TableName && greekUtils.toGreeklish(String(item.Table.TableName).toLocaleLowerCase(this.$i18n.locale)),
        item.Telephone && String(item.Telephone),
        item.Telephone2 && String(item.Telephone2),
        item.Telephone3 && String(item.Telephone3)
      ].filter(Boolean).join(' ')

      return itemString.includes(searchString)
    },

    getPrintNotifyStatus(print) {
      const label = (this.getPrintNotifyType(print.Type)?.Title ?? print.Type) + (print.Group && ` [${print.Group}]`)
      const avatarIcon = print.ReceivedAt ? 'check' : 'close'
      const avatarColor = print.ReceivedAt ? 'success darken-1' : 'red darken-1'
      const color = this.getPrintNotifyType(print.Type).Color
      const tooltip = print.ReceivedAt ? '' : ''

      return {
        Label      : label,
        Tooltip    : tooltip,
        Color      : color,
        AvatarColor: avatarColor,
        AvatarIcon : avatarIcon
      }
    },

    getPrintNotifyType(type) {
      return this.$te(`Order.PrintNotifyType.${type}`) ? this.$t(`Order.PrintNotifyType.${type}`) : null
    },

    getDeliveryStaffById(deliveryStaffId) {
      return this.deliveryStaffList.find(staff => parseInt(staff.Id) === parseInt(deliveryStaffId))
    },
    deliveryStaffHasActiveShift(order) {
      return order && order.DeliveryStaffHasActiveShift
    },
    showRemoveOrderFromDeliveryStaffDialog(order) {
      if (order && this.deliveryStaffHasActiveShift(order)) {
        this.selectedOrder = order
        this.confirmDialogRemoveOrderFromDeliveryStaffVisible = true
      }
    },
    removeOrderFromDeliveryStaff() {
      const selectedDeliveryStaff = this.getDeliveryStaffById(this.selectedOrder.DeliveryStaffId)

      if (selectedDeliveryStaff && this.selectedOrder) {
        const selectedOrder = selectedDeliveryStaff.ShiftOrders.find(order => parseInt(order.Id) === parseInt(this.selectedOrder.Id))

        if (selectedOrder) {
          window.callAS(window.SocketCommand.Staff.Delivery.Shift.RemoveOrder, {
            DeliveryStaffShiftOrderId: selectedOrder.DeliveryStaffShiftOrderId,
            DeliveryStaffShiftId     : selectedDeliveryStaff.DeliveryStaffShiftId,
            OrderId                  : selectedOrder.Id
          }, window.SocketCommand.Staff.Delivery.All)
        }
      }
      this.selectedOrder = null
    },
    showSelectOrderTypeTimeDialog(order) {
      if (parseInt(order.StatusId) === OrderStatusEnum.Accepted) {
        this.executeWithPinPermissionManager(PinManagerEnum.PosChangeOrderTimeAfterAccept, (success) => {
          if (!success) return
          this.$bus.$emit('order-type-time-dialog', order)
        })
      } else {
        this.executeWithPinPermissionManager(PinManagerEnum.PosChangeOrderTimeDuringAccept, (success) => {
          if (!success) return
          this.$bus.$emit('order-type-time-dialog', order)
        })
      }
    },
    showAddOrderToDeliveryStaff(order) {
      this.selectedOrder = order
      this.selectedDeliveryStaffDialogVisible = true
    },
    showAcceptOrderDialog(order) {
      this.$bus.$emit('show-order-accept-dialog', order)
    },
    showOrderMapDialog() {
      this.ordersMapDialogVisible = true
    },

    orderPaymentDialogAvailable(order) {
      if (
        SourceTypeEnum.ONLINE_SOURCES.includes(parseInt(order.SourceTypeId)) ||
          SourceTypeEnum.TABLE === parseInt(order.SourceTypeId) ||
          order.StatusId === OrderStatusEnum.Canceled ||
          (order.DeletedAt && order.StatusId === OrderStatusEnum.Canceled) ||
          !!order.ExternalSourceTypeId ||
          this.orderHasFiscalDocument(order)
      ) {
        return false
      }

      return true
    },

    showOrderPaymentDialog(order) {
      this.selectedOrder = order

      this.executeWithPinPermissionManager(PinManagerEnum.PosPayments, (success) => {
        if (!success) return
        this.orderPaymentDialogVisible = true
      })
    },

    onPaymentTransactionResult(data) {
      this.getData({orderId: data?.order_id || null})
    },

    getData(payload = {}) {
      this.getDataInternal(payload)
      window.callAS(window.SocketCommand.Order.Counts, {})
      // console.log((new Error()).stack)
    },

    onUpdateSelectedOrder(data, payload) {
      if (!payload?.orderId || !this.selectedOrder) return

      const order = data?.find(order => parseInt(order.Id) === parseInt(payload.orderId))
      if (this.selectedOrder?.Id === order?.Id) this.selectedOrder = order
    }
  }
}
</script>

<style scoped>
:deep(.receipts-badge > .v-badge__wrapper > span) {
  height: 16px;
  min-width: 16px;
  font-size: 11px;
  font-weight: bold;
  line-height: 8px;
  padding: 5px 4px;
}
</style>
